import * as Sentry from "@sentry/react"

// Browser page translators (Google Translate, Edge) wrap React-owned text
// nodes in <font> tags, making React's removeChild/insertBefore throw and
// unmount the app through the ErrorBoundary. Fail soft instead so translated
// pages keep working. See https://github.com/facebook/react/issues/11538
const reported = new Set<string>()

const reportSkip = (method: "removeChild" | "insertBefore", initialLang: string) => {
  const html = document.documentElement
  const translated = html.classList.contains("translated-ltr")
    || html.classList.contains("translated-rtl")
    || html.lang !== initialLang
  const key = `${method}:${translated}`

  if (reported.has(key)) return
  reported.add(key)

  Sentry.captureMessage(`Browser translation guard skipped ${method}`, {
    level: "warning",
    tags: { translationGuard: method, pageTranslated: String(translated) },
  })
}

const installBrowserTranslationGuard = () => {
  if (typeof Node !== "function" || !Node.prototype) return

  const initialLang = document.documentElement.lang

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportSkip("removeChild", initialLang)

      return child
    }

    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportSkip("insertBefore", initialLang)

      return newNode
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}

export default installBrowserTranslationGuard
